<template>
  <div>
    <div class="flex items-center justify-between">
        <div>
            <h2 class="mb-0">
                {{ subjectArea.name }}
            </h2>
            <p>
                <u><a :href="'/api/subject-areas/content-listings/'+subjectArea.id+'/quiz-reports/grades/excel/'">Download quiz grades report</a></u> (will be empty if no quizzes available in this subject area)
            </p>
            <p>
                <u><a :href="'/api/subject-areas/content-listings/'+subjectArea.id+'/exam-reports/grades/excel/'">Download exam grades report</a></u> (will be empty if no exams available in this subject area)
            </p>
        </div>
      <div class="flex items-center">
        <!-- Search Filter -->
        <div class="dropdown dropdown-end">
          <d-btn flat size="sm">
            <d-icon icon="filter" />
          </d-btn>
          <div tabindex="0" class="dropdown-content z-[1] card card-compact w-64 p-4 shadow bg-white">
            <d-field label="Search by Name">
              <d-input 
                v-model="filter" 
                placeholder="Type to search..."
                size="sm"
              />
            </d-field>
          </div>
        </div>
        
        <d-btn color="error" flat class="mr-3" @click="showResetAllProgressModal = true">
          <d-icon icon="refresh" class="mr-1" />
          Reset All Progress for a User
        </d-btn>
        <d-btn color="secondary-500" @click="showAddContentModal = true">
          + Add Content
        </d-btn>
      </div>
    </div>
    <d-table 
        class="bg-white shadow mt-5 mb-5" 
        :data="listings" 
        :columns="columns"
        :loading="loading" 
        backend-search 
        backend-sorting 
        :default-sort="[pagination.sortField, pagination.sortOrder]" 
        striped 
        detail-key="id" 
        default-sort-direction="desc" 
        @sort="onSort"
    >
        <template v-slot="{ row }">
            <td>
                <a class="underline" :href="`/subject-areas/listing/${subjectArea.id}/edit/${row.id}`">{{ row.name }}</a>
            </td>
            <td>
                <d-input 
                    v-model.number="row.order" 
                    type="number" 
                    min="1" 
                    class="w-16"
                    @blur="updateOrder(row.id, row.order)"
                />
            </td>
            <td>
                <button
                    :class="['btn btn-sm', row.pinned ? 'btn-warning gap-1' : 'btn-ghost']"
                    :title="row.pinned ? 'Pinned to top — click to unpin' : 'Pin resource to top'"
                    @click="togglePin(row)"
                >
                    <d-icon icon="thumbtack" :class="row.pinned ? '' : 'text-gray-600'" />
                    <span v-if="row.pinned" class="text-xs font-semibold">Pinned</span>
                </button>
            </td>
            <td>{{ row.type }}</td>
            <td>{{ formatShortDate(row.createdAt) }}</td>
            <td>{{ row.authorName }}</td>
            <td>
                <d-icon v-if="row.active" icon="check" class="text-green" />
                <div v-else />
            </td>
  
            <td>
                <d-icon icon="users" class="cursor-pointer ml-1 text-lg" @click="openPromoCodeModal(row)" />
            </td>
            <td>
                <d-icon icon="download" class="cursor-pointer ml-1 text-lg" @click="downloadInteractionReport(row)" />
                <d-icon icon="envelope" class="cursor-pointer ml-3 text-lg" @click="emailInteractionReport(row)" />
            </td>
            <td>
                <a v-if="row.hasQuizzes" :href="`/subject-areas/listing/edit/${row.id}/quiz-report`">
                    <d-icon icon="share" class="ml-2 text-lg" />
                </a>
                <span v-else>N/A</span>
            </td>
            <td>
                <d-icon icon="copy" class="cursor-pointer ml-1 text-lg" @click="openDuplicateContentModal(row)" />
            </td>
            <td>
                <d-icon icon="refresh" class="cursor-pointer ml-1 text-lg text-orange-600" @click="openResetProgressModal(row)" />
            </td>
            <td>
                <a :href="`/subject-areas/listing/${subjectArea.id}/edit/${row.id}`">
                    <d-icon icon="edit" class="ml-2 text-lg" />
                </a>
            </td>
            <td>
                <d-icon icon="trash" class="cursor-pointer text-error ml-3 text-lg" @click="showDeletePrompt(row)" />
            </td>
        </template>
    </d-table>

    <div class="flex justify-end">
      <d-pagination :total-items="total" :per-page="pagination.pageSize" v-model:current="pagination.page" />
    </div>

    <!-- Add Content Modal -->
    <SubjectAreaContentModal
      :active="showAddContentModal"
      :subject-area="subjectArea"
      @close="showAddContentModal = false; getPaged()"
    />

    <!-- Promo Code Modal -->
    <PromoCodeModal
      :active="showPromoCodeModal"
      :content-listing="selectedContentListing"
      @close="showPromoCodeModal = false"
    />

    <!-- Duplicate Content Modal -->
    <DuplicateContentModal
      :active="showDuplicateContentModal"
      :content-listing="selectedContentListing"
      @close="showDuplicateContentModal = false; getPaged()"
    />

    <!-- Reset User Progress Modal -->
    <ResetUserProgressModal
      :active="showResetProgressModal"
      :content-listing="selectedContentListing"
      @close="showResetProgressModal = false"
    />

    <!-- Reset All User Progress Modal -->
    <ResetAllUserProgressModal
      :active="showResetAllProgressModal"
      :subject-area="subjectArea"
      @close="showResetAllProgressModal = false"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :active="deleteModalActive"
      :title="'Delete Content'"
      :message="`Are you sure you want to delete <strong>${contentToDelete?.name}</strong>?<br><br>This action cannot be undone.`"
      :confirm-text="'Delete Content'"
      @close="closeDeleteModal"
      @confirm="handleDeleteConfirmed"
    />
  </div>
</template>

<script>
  import axios from 'axios';
  import debounce from 'lodash/debounce';
  import { showErrorToast } from '@services/errorHandling.service';
  import { toast } from '@services/toast.service';
  import SubjectAreaContentModal from './components/SubjectAreaContentModal.vue';
  import PromoCodeModal from './components/PromoCodeModal.vue';
  import DuplicateContentModal from './components/DuplicateContentModal.vue';
  import ResetUserProgressModal from './components/ResetUserProgressModal.vue';
  import ResetAllUserProgressModal from './components/ResetAllUserProgressModal.vue';
  import ConfirmModal from '../Users/components/ConfirmModal.vue';
  import { formatShortDate } from '@utils/formatters';

  export default {
    parent: true,

    components: {
      SubjectAreaContentModal,
      PromoCodeModal,
      DuplicateContentModal,
      ResetUserProgressModal,
      ResetAllUserProgressModal,
      ConfirmModal,
    },

    props: {
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        listings: [],
        loading: false,
        filter: '',
        total: 0,
        showAddContentModal: false,
        showPromoCodeModal: false,
        showDuplicateContentModal: false,
        showResetProgressModal: false,
        showResetAllProgressModal: false,
        deleteModalActive: false,
        selectedContentListing: {},
        contentToDelete: null,
        pagination: {
          page: 1,
          pageSize: 20,
          sortField: 'order',
          sortOrder: 'asc',
        },
      };
    },

    computed: {
        columns() {
            return [
                { field: 'name', label: 'Name', sortable: true },
                { field: 'order', label: 'Order' },
                { field: 'pin', label: 'Pinned' },
                { field: 'type', label: 'Type', sortable: true },
                { field: 'createdAt', label: 'Created Date', sortable: true },
                { field: 'authorName', label: 'Author', sortable: true },
                { field: 'active', label: 'Active?', sortable: true },
                { field: 'users', label: 'Promo Codes' },
                { field: 'download', label: 'Interaction Report' },
                { field: 'quiz_results', label: 'Quiz/Exam Results' },
                { field: 'copy', label: 'Duplicate' },
                { field: 'reset_progress', label: 'Reset User Progress' },
                { field: 'edit', label: 'Edit' },
                { field: 'delete', label: 'Delete' },
            ];
        }
    },

    watch: {
      pagination: {
        handler() {
          this.getPaged();
        },
        deep: true,
      },
      filter() {
        this.getDebouncedPaged();
      },
    },

    mounted() {
      this.getPaged();
    },

    methods: {
      formatShortDate,

      async getPaged() {
        const {
          page, sortField, sortOrder, pageSize,
        } = this.pagination;
        const params = [`filter=${this.filter}`, `sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`].join('&');
        this.loading = true;
        const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/content-listings?${params}`);
        this.listings = data.resultItems;
        this.total = data.totalRecords;
        this.loading = false;
      },

      // eslint-disable-next-line func-names
      getDebouncedPaged: debounce(function () {
        this.getPaged();
      }, 500),

      downloadInteractionReport(contentListing) {
        window.location = `/api/content-listing/${contentListing.id}/interactions-report`;
        },

      async  emailInteractionReport(contentListing) {
            await axios.get(`/api/content-listing/${contentListing.id}/interactions-report/email`)
            toast.success('You should have the report in your inbox within a few minutes');
        },

      openPromoCodeModal(contentListing) {
        this.selectedContentListing = contentListing;
        this.showPromoCodeModal = true;
      },

      openDuplicateContentModal(contentListing) {
        this.selectedContentListing = contentListing;
        this.showDuplicateContentModal = true;
      },

      openResetProgressModal(contentListing) {
        this.selectedContentListing = contentListing;
        this.showResetProgressModal = true;
      },

      showDeletePrompt(content) {
        this.contentToDelete = content;
        this.deleteModalActive = true;
      },

      closeDeleteModal() {
        this.deleteModalActive = false;
        this.contentToDelete = null;
      },

      async handleDeleteConfirmed() {
        if (!this.contentToDelete) return;
        
        try {
          await axios.delete(`/api/content-listing/${this.contentToDelete.id}`);
          await this.getPaged();
          toast.success('Content was deleted');
        } catch (error) {
          showErrorToast(error);
        } finally {
          this.closeDeleteModal();
        }
      },

      onSort(field, order) {
        this.pagination.sortField = field;
        this.pagination.sortOrder = order;
      },

      async updateOrder(id, newOrder) {
        try {
          await axios.post('/api/content-listings/update-order', {
            orderUpdates: [{ id, order: newOrder }]
          });
          toast.success('Order updated successfully');
        } catch (error) {
          showErrorToast(error);
          this.getPaged(); // Reload to revert changes
        }
      },

      async togglePin(listing) {
        try {
          const { data } = await axios.post(`/api/content-listings/${listing.id}/pin`);
          listing.pinned = data.pinned;
          toast.success(data.pinned ? 'Resource pinned to top' : 'Resource unpinned');
          this.getPaged();
        } catch (error) {
          showErrorToast(error);
        }
      },
    },
  };
</script>
